/**
 * ExplainabilityCard Component
 *
 * Inline expandable "Why?" explanation for recommendations and verdicts.
 * Used throughout the simulator to explain model outputs in natural language.
 *
 * TASK-2026-072: Explainability Layer - Phase 1 Trust & Workflow
 */

import { useState } from 'react';

// Icons
const ChevronDownIcon = ({ className = '' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const LightbulbIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export interface ExplainabilityFactor {
  label: string;
  value: string | number;
  impact: 'positive' | 'negative' | 'neutral';
  contribution?: number; // percentage contribution to outcome
}

export interface ExplainabilityCardProps {
  title?: string;
  summary: string;
  factors?: ExplainabilityFactor[];
  insights?: string[];
  nextSteps?: string[];
  methodology?: string;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  defaultExpanded?: boolean;
  className?: string;
}

export function ExplainabilityCard({
  title = 'Why?',
  summary,
  factors = [],
  insights = [],
  nextSteps = [],
  methodology,
  variant = 'default',
  defaultExpanded = false,
  className = '',
}: ExplainabilityCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const variantStyles = {
    default: {
      border: 'border-surface-600',
      bg: 'bg-surface-700/30',
      header: 'text-text-secondary hover:text-white',
      icon: 'text-primary-400',
    },
    success: {
      border: 'border-green-500/30',
      bg: 'bg-green-500/5',
      header: 'text-green-400 hover:text-green-300',
      icon: 'text-green-400',
    },
    warning: {
      border: 'border-amber-500/30',
      bg: 'bg-amber-500/5',
      header: 'text-amber-400 hover:text-amber-300',
      icon: 'text-amber-400',
    },
    danger: {
      border: 'border-red-500/30',
      bg: 'bg-red-500/5',
      header: 'text-red-400 hover:text-red-300',
      icon: 'text-red-400',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={`rounded-lg border ${styles.border} ${styles.bg} ${className}`}>
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-3 ${styles.header} transition-colors`}
      >
        <div className="flex items-center gap-2">
          <span className={styles.icon}>
            <LightbulbIcon />
          </span>
          <span className="text-sm font-medium">{title}</span>
        </div>
        <ChevronDownIcon
          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-4">
          {/* Summary */}
          <p className="text-sm text-text-secondary leading-relaxed">
            {summary}
          </p>

          {/* Key Factors */}
          {factors.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
                Key Factors
              </h4>
              <div className="space-y-1.5">
                {factors.map((factor, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          factor.impact === 'positive'
                            ? 'bg-green-400'
                            : factor.impact === 'negative'
                            ? 'bg-red-400'
                            : 'bg-gray-400'
                        }`}
                      />
                      <span className="text-text-secondary">{factor.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${
                          factor.impact === 'positive'
                            ? 'text-green-400'
                            : factor.impact === 'negative'
                            ? 'text-red-400'
                            : 'text-white'
                        }`}
                      >
                        {typeof factor.value === 'number'
                          ? factor.value > 0
                            ? `+${factor.value}%`
                            : `${factor.value}%`
                          : factor.value}
                      </span>
                      {factor.contribution !== undefined && (
                        <span className="text-xs text-text-tertiary">
                          ({factor.contribution}% weight)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Insights */}
          {insights.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
                Key Insights
              </h4>
              <ul className="space-y-1.5">
                {insights.map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-primary-400 mt-0.5">
                      <CheckCircleIcon />
                    </span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Next Steps */}
          {nextSteps.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
                Recommended Next Steps
              </h4>
              <ul className="space-y-1.5">
                {nextSteps.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-amber-400 mt-0.5">
                      <ArrowRightIcon />
                    </span>
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Methodology note */}
          {methodology && (
            <div className="pt-2 border-t border-surface-600">
              <p className="text-xs text-text-tertiary italic">
                <span className="font-medium">Methodology:</span> {methodology}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Helper function to generate explanation for Launch Verdicts
 */
export function generateLaunchExplanation(
  verdict: 'go' | 'refine' | 'pause',
  data: {
    netIncremental?: number;
    cannibalizationRisk?: number;
    marketShare?: number;
    trialRate?: number;
    repeatRate?: number;
    highRiskSKUs?: number;
  }
): ExplainabilityCardProps {
  const {
    netIncremental = 0,
    cannibalizationRisk = 0,
    marketShare = 0,
    trialRate = 0,
    repeatRate = 0,
    highRiskSKUs = 0,
  } = data;

  const factors: ExplainabilityFactor[] = [];
  const insights: string[] = [];
  const nextSteps: string[] = [];
  let summary = '';
  let variant: 'success' | 'warning' | 'danger' = 'warning';

  if (verdict === 'go') {
    variant = 'success';
    summary = `This launch shows strong potential with ${Math.round(netIncremental * 100)}% incremental volume and minimal cannibalization risk. The concept demonstrates healthy trial and repeat metrics.`;

    factors.push(
      { label: 'Net Incremental Volume', value: Math.round(netIncremental * 100), impact: 'positive' },
      { label: 'Projected Market Share', value: marketShare, impact: 'positive' },
      { label: 'Cannibalization Risk', value: -Math.round(cannibalizationRisk * 100), impact: cannibalizationRisk > 0.05 ? 'negative' : 'positive' }
    );

    insights.push(
      `Volume is primarily incremental (${Math.round(netIncremental * 100)}%), not cannibalized`,
      `Trial rate of ${Math.round(trialRate * 100)}% is above category benchmark`,
      highRiskSKUs === 0 ? 'No high-risk cannibalization pairs identified' : `${highRiskSKUs} high-risk overlap pairs require monitoring`
    );

    nextSteps.push(
      'Proceed to concept testing with target consumers',
      'Finalize distribution and retail execution plan',
      'Prepare launch timeline and marketing calendar'
    );
  } else if (verdict === 'refine') {
    variant = 'warning';
    summary = `This launch shows promise but requires refinement. ${cannibalizationRisk > 0.1 ? 'Cannibalization risk is elevated. ' : ''}Consider adjusting positioning or timing to maximize incremental volume.`;

    factors.push(
      { label: 'Net Incremental Volume', value: Math.round(netIncremental * 100), impact: netIncremental > 0.4 ? 'positive' : 'negative' },
      { label: 'Cannibalization Risk', value: -Math.round(cannibalizationRisk * 100), impact: 'negative' },
      { label: 'Projected Market Share', value: marketShare, impact: marketShare > 1.5 ? 'positive' : 'neutral' }
    );

    insights.push(
      cannibalizationRisk > 0.1 ? `Cannibalization (${Math.round(cannibalizationRisk * 100)}%) exceeds 10% threshold` : 'Cannibalization within acceptable range',
      `Repeat rate of ${Math.round(repeatRate * 100)}% suggests product-market fit concerns`,
      highRiskSKUs > 0 ? `${highRiskSKUs} SKUs have high overlap - consider differentiation` : 'No critical overlap issues'
    );

    nextSteps.push(
      'Review positioning to differentiate from existing portfolio',
      'Conduct additional consumer research on key concerns',
      'Consider phased rollout to monitor actual cannibalization'
    );
  } else {
    variant = 'danger';
    summary = `This launch is not recommended in its current form. Insufficient incremental volume (${Math.round(netIncremental * 100)}%) and/or high cannibalization risk suggest the concept needs significant rework.`;

    factors.push(
      { label: 'Net Incremental Volume', value: Math.round(netIncremental * 100), impact: 'negative' },
      { label: 'Cannibalization Risk', value: -Math.round(cannibalizationRisk * 100), impact: 'negative' },
      { label: 'High-Risk SKU Overlaps', value: highRiskSKUs, impact: 'negative' }
    );

    insights.push(
      `Majority of volume (${Math.round((1 - netIncremental) * 100)}%) comes from own portfolio`,
      'Concept does not sufficiently differentiate from existing offerings',
      'Risk of net share loss outweighs potential gains'
    );

    nextSteps.push(
      'Revisit concept positioning and target segment',
      'Explore alternative product configurations',
      'Consider optimizing existing portfolio before new launches'
    );
  }

  return {
    title: 'Why this verdict?',
    summary,
    factors,
    insights,
    nextSteps,
    methodology: 'Based on Parfitt-Collins trial/repeat model and source-of-volume decomposition',
    variant,
  };
}

/**
 * Helper function to generate explanation for Portfolio Rationalization
 */
export function generatePortfolioExplanation(
  action: 'keep' | 'reposition' | 'delist',
  data: {
    skuName?: string;
    totalScore?: number;
    shareScore?: number;
    marginScore?: number;
    growthScore?: number;
    cannibalizationScore?: number;
    strategicImportance?: number;
  }
): ExplainabilityCardProps {
  const {
    skuName = 'This SKU',
    totalScore = 0,
    shareScore = 0,
    marginScore = 0,
    growthScore = 0,
    cannibalizationScore = 0,
    strategicImportance = 0,
  } = data;

  const factors: ExplainabilityFactor[] = [
    { label: 'Volume Share', value: shareScore, impact: shareScore >= 60 ? 'positive' : shareScore >= 40 ? 'neutral' : 'negative', contribution: 25 },
    { label: 'Margin Contribution', value: marginScore, impact: marginScore >= 60 ? 'positive' : marginScore >= 40 ? 'neutral' : 'negative', contribution: 20 },
    { label: 'Growth Trend', value: growthScore, impact: growthScore >= 60 ? 'positive' : growthScore >= 40 ? 'neutral' : 'negative', contribution: 20 },
    { label: 'Cannibalization', value: cannibalizationScore, impact: cannibalizationScore >= 60 ? 'positive' : 'negative', contribution: 20 },
    { label: 'Strategic Importance', value: strategicImportance, impact: strategicImportance >= 60 ? 'positive' : 'neutral', contribution: 15 },
  ];

  let summary = '';
  let variant: 'success' | 'warning' | 'danger' = 'warning';
  const insights: string[] = [];
  const nextSteps: string[] = [];

  if (action === 'keep') {
    variant = 'success';
    summary = `${skuName} is a core performer with a health score of ${Math.round(totalScore)}. It contributes positively to portfolio objectives and should be maintained.`;

    insights.push(
      shareScore >= 60 ? 'Strong volume contribution to portfolio' : 'Moderate volume share',
      marginScore >= 60 ? 'Above-average margin contribution' : 'Acceptable margin profile',
      growthScore >= 50 ? 'Positive or stable growth trajectory' : 'Consider growth initiatives'
    );

    nextSteps.push(
      'Monitor for early signs of decline',
      'Consider line extensions if segment is growing',
      'Protect shelf space and distribution'
    );
  } else if (action === 'reposition') {
    variant = 'warning';
    summary = `${skuName} has moderate performance (score: ${Math.round(totalScore)}) but shows potential. Consider repositioning to improve contribution.`;

    const weakAreas: string[] = [];
    if (marginScore < 40) weakAreas.push('margin');
    if (growthScore < 40) weakAreas.push('growth');
    if (cannibalizationScore < 40) weakAreas.push('cannibalization');

    insights.push(
      `Primary concerns: ${weakAreas.length > 0 ? weakAreas.join(', ') : 'overall performance'}`,
      cannibalizationScore < 50 ? 'High overlap with other SKUs in portfolio' : 'Moderate overlap risk',
      'Has potential but needs optimization'
    );

    nextSteps.push(
      marginScore < 40 ? 'Review pricing or cost structure' : 'Maintain current pricing',
      cannibalizationScore < 50 ? 'Differentiate positioning from overlapping SKUs' : 'Current positioning acceptable',
      'Test repositioning in limited market before full rollout'
    );
  } else {
    variant = 'danger';
    summary = `${skuName} is underperforming (score: ${Math.round(totalScore)}) and should be considered for delisting. Volume may shift to healthier SKUs.`;

    insights.push(
      'Low contribution relative to portfolio average',
      cannibalizationScore < 40 ? 'High cannibalization with better-performing SKUs' : 'Limited differentiation',
      'Delisting may improve overall portfolio efficiency'
    );

    nextSteps.push(
      'Validate with sales team on customer dependencies',
      'Plan transition for any loyal customers',
      'Execute gradual phase-out to minimize disruption'
    );
  }

  return {
    title: `Why "${action}"?`,
    summary,
    factors,
    insights,
    nextSteps,
    methodology: 'Multi-factor scoring: share (25%), margin (20%), growth (20%), cannibalization (20%), strategic (15%)',
    variant,
  };
}

/**
 * Helper function to generate explanation for Elasticity outputs
 */
export function generateElasticityExplanation(
  elasticity: number,
  data: {
    productName?: string;
    category?: string;
    isPremium?: boolean;
    hasPromoHistory?: boolean;
  }
): ExplainabilityCardProps {
  const {
    productName = 'This product',
    category = 'CPG',
    isPremium = false,
    hasPromoHistory = false,
  } = data;

  const isElastic = Math.abs(elasticity) > 1.5;
  const isInelastic = Math.abs(elasticity) < 1;

  let summary = '';
  let variant: 'success' | 'warning' | 'danger' = 'default' as any;
  const insights: string[] = [];
  const nextSteps: string[] = [];

  if (isInelastic) {
    variant = 'success';
    summary = `${productName} has low price sensitivity (elasticity: ${elasticity.toFixed(2)}). Price increases will have limited volume impact, making this a strong candidate for value extraction.`;

    insights.push(
      `Elasticity of ${elasticity.toFixed(2)} is below category average of -2.0`,
      isPremium ? 'Premium positioning supports price insensitivity' : 'Strong brand loyalty despite mainstream positioning',
      'Volume loss from 10% price increase: ~' + Math.abs(Math.round(elasticity * 10)) + '%'
    );

    nextSteps.push(
      'Consider modest price increases to capture value',
      'Focus promotions on trial, not price discounts',
      'Monitor competitor pricing for relative position'
    );
  } else if (isElastic) {
    variant = 'warning';
    summary = `${productName} has high price sensitivity (elasticity: ${elasticity.toFixed(2)}). Price changes will significantly impact volume. Be cautious with increases.`;

    insights.push(
      `Elasticity of ${elasticity.toFixed(2)} exceeds category average`,
      hasPromoHistory ? 'Historical promotions may have trained price sensitivity' : 'Category is inherently price-sensitive',
      'Volume loss from 10% price increase: ~' + Math.abs(Math.round(elasticity * 10)) + '%'
    );

    nextSteps.push(
      'Avoid leading price increases in category',
      'Focus on non-price value drivers (quality, convenience)',
      'Consider pack size optimization instead of price changes'
    );
  } else {
    variant = 'default' as any;
    summary = `${productName} has moderate price sensitivity (elasticity: ${elasticity.toFixed(2)}), typical for ${category}. Standard pricing strategies apply.`;

    insights.push(
      `Elasticity of ${elasticity.toFixed(2)} is within normal range for ${category}`,
      'Responds predictably to price changes',
      'Volume loss from 10% price increase: ~' + Math.abs(Math.round(elasticity * 10)) + '%'
    );

    nextSteps.push(
      'Maintain pricing in line with category dynamics',
      'Use promotions strategically for volume spikes',
      'Monitor for elasticity changes over time'
    );
  }

  const factors: ExplainabilityFactor[] = [
    { label: 'Price Elasticity', value: elasticity.toFixed(2), impact: isInelastic ? 'positive' : isElastic ? 'negative' : 'neutral' },
    { label: '10% Price Impact', value: Math.round(elasticity * 10), impact: 'neutral' },
  ];

  return {
    title: 'Elasticity Interpretation',
    summary,
    factors,
    insights,
    nextSteps,
    methodology: 'Elasticity estimated from historical price-volume relationships and category benchmarks',
    variant: variant as 'success' | 'warning' | 'danger',
  };
}
